// The home page's cinematic background shader (fBm domain-warp + Voronoi
// cells + a raymarched, smooth-min blob of 5 golden-angle-orbiting spheres —
// see assets/cinematic-experience.js) reused as a self-contained background
// for a single ritual hero banner, without that file's homepage-specific
// 3-act scroll narrative, particle field, or camera drift. Here uMorph
// (0=Clarity, 1=Energy, 2=Balance) drifts on its own so the banner is never
// static, and scrolling the banner in/out of view nudges it further — same
// idea as the home page, scaled down to one hero section.
import * as THREE from "https://cdn.jsdelivr.net/npm/three@0.180.0/build/three.module.js";

const PALETTE = {
  warmWhite: 0xfdfaf5,
  sand: 0xe8dfd0,
  sage: 0x7a9471,
  moss: 0x3d4f3a,
  charcoal: 0x2a2a25,
  stone: 0xa89a86,
};

const VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const SIMPLEX_3D = /* glsl */ `
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);

    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);

    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;

    i = mod289(i);
    vec4 p = permute(permute(permute(
              i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));

    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);

    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);

    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);

    vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

    vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
  }
`;

const FRAGMENT = /* glsl */ `
  precision highp float;
  varying vec2 vUv;

  uniform float uTime;
  uniform float uMorph;
  uniform vec2 uResolution;
  uniform vec3 uColorWarmWhite;
  uniform vec3 uColorSand;
  uniform vec3 uColorSage;
  uniform vec3 uColorMoss;
  uniform vec3 uColorCharcoal;
  uniform vec3 uColorStone;

  ${SIMPLEX_3D}

  float fbm(vec3 p) {
    float sum = 0.0;
    float amp = 0.5;
    float freq = 1.0;
    for (int i = 0; i < 3; i++) {
      sum += amp * snoise(p * freq);
      freq *= 2.02;
      amp *= 0.5;
    }
    return sum;
  }

  float warpedFbm(vec3 p) {
    vec3 q = vec3(
      fbm(p + vec3(0.0, 0.0, uTime * 0.03)),
      fbm(p + vec3(5.2, 1.3, uTime * 0.025)),
      fbm(p + vec3(1.7, 9.2, uTime * 0.02))
    );
    return fbm(p + 4.0 * q);
  }

  vec2 voronoi(vec2 x) {
    vec2 n = floor(x);
    vec2 f = fract(x);
    float minDist = 8.0;
    vec2 minPoint;
    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 neighbor = vec2(float(i), float(j));
        vec2 point = 0.5 + 0.5 * sin(uTime * 0.05 + 6.2831 * snoise(vec3(n + neighbor, 0.0)) * vec2(1.0, 1.3));
        vec2 diff = neighbor + point - f;
        float dist = length(diff);
        if (dist < minDist) {
          minDist = dist;
          minPoint = point;
        }
      }
    }
    return vec2(minDist, snoise(vec3(n + minPoint, 1.0)));
  }

  float smin(float a, float b, float k) {
    float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
    return mix(b, a, h) - k * h * (1.0 - h);
  }

  float sdSphere(vec3 p, float r) {
    return length(p) - r;
  }

  float sceneSDF(vec3 p) {
    float d = 1e5;
    const float GOLDEN_ANGLE = 2.399963229728653;
    for (int i = 0; i < 5; i++) {
      float fi = float(i);
      float angle = fi * GOLDEN_ANGLE + uTime * 0.15 + uMorph * 1.4;
      float orbitRadius = 0.55 + 0.12 * sin(uTime * 0.2 + fi * 1.7);
      float height = sin(fi * 1.9 + uTime * 0.12 + uMorph) * 0.35;
      vec3 center = vec3(cos(angle) * orbitRadius, height, sin(angle) * orbitRadius);
      float r = 0.32 + 0.08 * sin(uTime * 0.3 + fi);
      d = smin(d, sdSphere(p - center, r), 0.45);
    }
    return d;
  }

  vec3 sceneNormal(vec3 p) {
    float e = 0.001;
    return normalize(vec3(
      sceneSDF(p + vec3(e, 0.0, 0.0)) - sceneSDF(p - vec3(e, 0.0, 0.0)),
      sceneSDF(p + vec3(0.0, e, 0.0)) - sceneSDF(p - vec3(0.0, e, 0.0)),
      sceneSDF(p + vec3(0.0, 0.0, e)) - sceneSDF(p - vec3(0.0, 0.0, e))
    ));
  }

  vec4 raymarchBlob(vec3 ro, vec3 rd) {
    float t = 0.0;
    for (int i = 0; i < 36; i++) {
      vec3 p = ro + rd * t;
      float d = sceneSDF(p);
      if (d < 0.004) {
        vec3 n = sceneNormal(p);
        vec3 lightDir = normalize(vec3(0.5, 0.8, 0.6));
        float diffuse = max(dot(n, lightDir), 0.0);
        float fresnel = pow(1.0 - max(dot(n, -rd), 0.0), 2.5);
        return vec4(n, diffuse + fresnel * 0.6);
      }
      t += d;
      if (t > 6.0) break;
    }
    return vec4(0.0);
  }

  void main() {
    vec2 uv = vUv;
    vec2 p = (uv - 0.5) * vec2(uResolution.x / uResolution.y, 1.0);

    vec3 clarityColor = mix(uColorWarmWhite, uColorSand, 0.5);
    vec3 energyColor = mix(uColorSage, uColorStone, 0.45);
    vec3 balanceColor = mix(uColorMoss, uColorCharcoal, 0.55);

    vec3 baseColor;
    if (uMorph < 1.0) {
      baseColor = mix(clarityColor, energyColor, smoothstep(0.0, 1.0, uMorph));
    } else {
      baseColor = mix(energyColor, balanceColor, smoothstep(0.0, 1.0, uMorph - 1.0));
    }

    float flow = warpedFbm(vec3(p * 1.6, uTime * 0.04 + uMorph * 0.6));

    vec2 vor = voronoi(p * 3.2 + uTime * 0.02);
    float cells = smoothstep(0.0, 0.08, vor.x);

    float camAngle = uTime * 0.06 + uMorph * 0.3;
    vec3 ro = vec3(sin(camAngle) * 2.4, 0.3, cos(camAngle) * 2.4);
    vec3 target = vec3(0.0);
    vec3 fwd = normalize(target - ro);
    vec3 right = normalize(cross(fwd, vec3(0.0, 1.0, 0.0)));
    vec3 up = cross(right, fwd);
    vec3 rd = normalize(fwd * 1.8 + right * p.x + up * p.y);
    vec4 blob = raymarchBlob(ro, rd);

    vec3 color = baseColor;
    color = mix(color, mix(baseColor, uColorStone, 0.35), flow * 0.5 + 0.5);
    color *= mix(0.96, 1.04, cells);

    if (blob.a > 0.0) {
      vec3 blobColor = mix(uColorSage, uColorMoss, uMorph * 0.35);
      color = mix(color, blobColor, clamp(blob.a, 0.0, 1.0) * 0.55);
    }

    float vignette = smoothstep(1.1, 0.25, length(p));
    color = mix(color * 0.94, color, vignette);

    // Keep the whole banner on the light side of the palette so overlaid
    // text stays legible without needing a heavy white scrim on top.
    color = mix(color, uColorWarmWhite, 0.3);

    gl_FragColor = vec4(color, 1.0);
  }
`;

function colorUniform(hex) {
  return { value: new THREE.Color(hex) };
}

function computeEffectivePixelRatio() {
  const dpr = window.devicePixelRatio || 1;
  const width = window.innerWidth;
  if (width >= 1400) return Math.min(dpr, 0.9);
  if (width >= 900) return Math.min(dpr, 1.2);
  return Math.min(dpr, 1.5);
}

class RitualHeroBackground {
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector("[data-ritual-hero-canvas]");
    this.running = false;
    this.scrollProgress = 0.5;
    this.targetScrollProgress = 0.5;

    if (!this.canvas || !window.WebGL2RenderingContext) return;

    try {
      this.setUpScene();
    } catch (err) {
      console.error("Ritual hero background failed to initialize:", err);
      return;
    }

    this.resize = this.resize.bind(this);
    this.tick = this.tick.bind(this);
    this.resize();
    window.addEventListener("resize", this.resize, { passive: true });

    this.clock = new THREE.Clock();
    this.renderer.render(this.scene, this.camera);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.bindScroll();
    this.bindVisibility();
  }

  setUpScene() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: false,
      alpha: false,
    });
    this.renderer.setPixelRatio(computeEffectivePixelRatio());

    this.scene = new THREE.Scene();
    this.camera = new THREE.Camera();

    this.uniforms = {
      uTime: { value: 0 },
      uMorph: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uColorWarmWhite: colorUniform(PALETTE.warmWhite),
      uColorSand: colorUniform(PALETTE.sand),
      uColorSage: colorUniform(PALETTE.sage),
      uColorMoss: colorUniform(PALETTE.moss),
      uColorCharcoal: colorUniform(PALETTE.charcoal),
      uColorStone: colorUniform(PALETTE.stone),
    };

    const material = new THREE.ShaderMaterial({
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      uniforms: this.uniforms,
      depthWrite: false,
      depthTest: false,
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    this.scene.add(quad);
  }

  resize() {
    const rect = this.root.getBoundingClientRect();
    const width = Math.max(rect.width, 1);
    const height = Math.max(rect.height, 1);
    this.renderer.setPixelRatio(computeEffectivePixelRatio());
    this.renderer.setSize(width, height, false);
    this.uniforms.uResolution.value.set(width, height);
  }

  bindScroll() {
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;
    ScrollTrigger.create({
      trigger: this.root,
      start: "top bottom",
      end: "bottom top",
      scrub: true,
      onUpdate: (self) => {
        this.targetScrollProgress = self.progress;
      },
    });
  }

  bindVisibility() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.start();
          else this.stop();
        });
      },
      { rootMargin: "200px 0px" }
    );
    this.observer.observe(this.root);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop() {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  tick() {
    if (!this.running) return;
    const elapsed = this.clock.getElapsedTime();

    // Ambient drift so the banner never sits still on its own, plus a
    // scroll-driven push so passing through the section visibly bends the
    // palette further — same "always lerping, never snapping" principle as
    // the home page's uMorph. Range is kept mostly within Clarity/Energy
    // (0..1.3) rather than the full 0..2 — Balance's moss/charcoal end
    // read too dark behind overlaid text on this hero banner.
    const autoMorph = 0.65 + Math.sin(elapsed * 0.05) * 0.65;
    this.scrollProgress += (this.targetScrollProgress - this.scrollProgress) * 0.05;
    const target = autoMorph + (this.scrollProgress - 0.5) * 0.6;
    this.uniforms.uMorph.value += (Math.min(Math.max(target, 0), 1.3) - this.uniforms.uMorph.value) * 0.04;
    this.uniforms.uTime.value = elapsed;

    this.renderer.render(this.scene, this.camera);
    this.rafId = requestAnimationFrame(this.tick);
  }
}

function setUp() {
  document.querySelectorAll("[data-ritual-hero-bg]").forEach((root) => {
    if (root.dataset.ritualHeroBound) return;
    root.dataset.ritualHeroBound = "true";
    new RitualHeroBackground(root);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setUp);
} else {
  setUp();
}
document.addEventListener("shopify:section:load", setUp);
